<script setup lang="ts">
import { ManyCurrentAndUpcomingOccurrencesByTypeQuery } from '~/queries/many/programme-types'

const props = defineProps<{
  title?: string
  description?: any
  useMask?: boolean | null
  image?: Media | null
  maskedMedia?: MaskedMedia | null
  viewOption?: 'title' | 'thumbnails'
  typeSlug: string
  typeId: string
}>()

const { locale } = useI18n()
// hour-rounded for the query (see cacheable-now.ts); the split below still uses
// the real time so an occurrence that ended ten minutes ago is not "current"
const now = cacheableNow()
const realNow = new Date()

// Sorted soonest-first, so page 1 (rendered on the server) holds what's on now
// and next; further-out dates stream in on the client.
const { docs: occurrences, loading } = await usePaginatedOccurrences(
  `current-upcoming-${props.typeSlug}-${locale.value}`,
  ManyCurrentAndUpcomingOccurrencesByTypeQuery,
  { now, typeId: props.typeId, locale: locale.value },
)

// A programme can have several recurrences, and each recurrence produces its own
// ProgrammeOccurrences doc. Group them back by programme so a programme with
// multiple current/upcoming dates renders as a single card instead of one per occurrence.
const groupByProgramme = (list: any[]) => {
  const bySlug = new Map<string, any>()
  for (const o of list) {
    const slug = o.programme?.slug
    if (!slug) continue
    if (!bySlug.has(slug)) {
      bySlug.set(slug, { id: slug, title: o.title, slug, recurrences: [], image: o.programme?.image, description: o.programme?.description })
    }
    bySlug.get(slug).recurrences.push({ dateTimeStart: o.dateTimeStart, dateTimeEnd: o.dateTimeEnd, id: o.id })
  }
  return [...bySlug.values()]
}

const current = computed(() =>
  groupByProgramme(
    occurrences.value.filter((o: any) => new Date(o.dateTimeStart) <= realNow && new Date(o.dateTimeEnd) >= realNow)
  )
)

const upcoming = computed(() =>
  groupByProgramme(
    occurrences.value.filter((o: any) => new Date(o.dateTimeStart) > realNow)
  )
)
</script>

<template>
  <section 
    class="mx-1"
    v-if="current.length || upcoming.length"
  >
    <h2 v-if="title" 
      class="text-d container-d"
      :class="{
        'mb-1 text-md': image?.id || maskedMedia?.id || description
      }"
    >
      {{ title }}
    </h2>

    <div 
      class="container-d mb-1"
      v-if="image?.id || maskedMedia?.id"
    >
      <core-maskable-media
        :use-mask="useMask"
        :image="image"
        :masked-media="maskedMedia"
      />
    </div>

    <div v-if="hasRichTextContent(description)" class="container-d mb-0_35">
      <core-rich-text :content="description" />
    </div>

    <div v-if="loading" class="container-d">
      {{ $t('labels.loading') }}
    </div>

    <div 
      v-if="!loading && current.length" 
      class="container-d"
    >
      <div
        v-if="current.length && upcoming.length"
      >
        {{ $t('labels.current') }}
      </div>
      <pages-programme-type-programme-list :programmes="current" :type-slug="typeSlug" :view-option="viewOption" />
    </div>

    <div 
      v-if="!loading && upcoming.length" 
      class="container-d"
    >
      <div
        v-if="current.length && upcoming.length"
      >
        {{ $t('labels.upcoming') }}
      </div>
      <pages-programme-type-programme-list :programmes="upcoming" :type-slug="typeSlug" :view-option="viewOption" />
    </div>
  </section>
</template>
